class SizeQuantityGridComponent extends HTMLElement {
  connectedCallback() {
    // The block schema's "tag" makes Shopify wrap this block in an outer
    // <size-quantity-grid-component>. Only the inner element carries data-product-id;
    // the wrapper must do nothing, or it binds the inner button too and every click
    // adds the garments to the cart twice.
    if (!this.dataset.productId) return;

    this.productId = this.dataset.productId;
    this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
    this.colourOptionIndex = parseInt(this.dataset.colourOptionIndex, 10);

    this.rowsEl = this.querySelector('[ref="rows"]');
    this.colourLabelEl = this.querySelector('[ref="colourLabel"]');
    this.totalLabelEl = this.querySelector('[ref="totalLabel"]');
    this.submitButton = this.querySelector('[ref="submitButton"]');
    this.noteEl = this.querySelector('[ref="noteEl"]');

    this._productForm = this._findProductForm();

    this._onVariantUpdate = () => this._render();
    document.addEventListener('variant:update', this._onVariantUpdate);

    // Attach the click handler only once. connectedCallback can run more than once
    // (when the theme moves or re-renders this block); adding a new anonymous
    // listener each time made one click add every item to the cart twice.
    this._onSubmitClick = this._onSubmitClick || (() => this._handleSubmit());
    this.submitButton?.removeEventListener('click', this._onSubmitClick);
    this.submitButton?.addEventListener('click', this._onSubmitClick);

    this._render();
  }

  disconnectedCallback() {
    if (!this.dataset.productId) return;
    document.removeEventListener('variant:update', this._onVariantUpdate);
    this.submitButton?.removeEventListener('click', this._onSubmitClick);
  }

  _findProductForm() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('form[data-type="add-to-cart-form"]') || null;
  }

  _getProductJsonData() {
    if (this._productJsonCache) return this._productJsonCache;
    const script = document.getElementById('tpo-store-data');
    if (!script) return null;
    try {
      this._productJsonCache = JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
    return this._productJsonCache;
  }

  _getCurrentVariantId() {
    const input = this._productForm?.querySelector('input[name="id"]');
    return input?.value || null;
  }

  _optionKey(index) {
    return 'option' + (index + 1);
  }

  _getCurrentColourValue(variants) {
    if (this.colourOptionIndex < 0) return null;
    const currentId = this._getCurrentVariantId();
    const current = variants.find((v) => String(v.id) === String(currentId));
    return current ? current[this._optionKey(this.colourOptionIndex)] : null;
  }

  _render() {
    const data = this._getProductJsonData();
    const variants = data?.product?.variants;
    if (!this.rowsEl || !variants) return;

    const colourValue = this._getCurrentColourValue(variants);
    const sizeKey = this._optionKey(this.sizeOptionIndex);
    const colourKey = this.colourOptionIndex >= 0 ? this._optionKey(this.colourOptionIndex) : null;

    const rowVariants = colourKey
      ? variants.filter((v) => v[colourKey] === colourValue)
      : variants;

    if (this.colourLabelEl) {
      this.colourLabelEl.textContent = colourValue ? `Sizes shown for ${colourValue}` : '';
    }

    this.rowsEl.innerHTML = '';
    rowVariants.forEach((variant) => {
      const row = document.createElement('div');
      row.className = 'size-quantity-grid__row';
      if (!variant.available) row.classList.add('size-quantity-grid__row--unavailable');

      const label = document.createElement('span');
      label.textContent = variant[sizeKey] || variant.title;
      row.appendChild(label);

      if (variant.available) {
        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.value = '0';
        input.dataset.variantId = variant.id;
        input.setAttribute('aria-label', `Quantity for size ${variant[sizeKey] || variant.title}`);
        input.addEventListener('input', () => this._updateTotal());
        row.appendChild(input);

        const price = document.createElement('span');
        price.className = 'size-quantity-grid__row-price';
        price.textContent = this._formatMoney(variant.price);
        row.appendChild(price);
      } else {
        const empty = document.createElement('span');
        row.appendChild(empty);
        const unavailable = document.createElement('span');
        unavailable.className = 'size-quantity-grid__row-unavailable-label';
        unavailable.textContent = 'Sold out';
        row.appendChild(unavailable);
      }

      this.rowsEl.appendChild(row);
    });

    this._updateTotal();
  }

  _formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  _getRowInputs() {
    return Array.from(this.rowsEl?.querySelectorAll('input[data-variant-id]') || []);
  }

  _updateTotal() {
    const total = this._getRowInputs().reduce((sum, input) => {
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      return sum + qty;
    }, 0);

    if (this.totalLabelEl) {
      this.totalLabelEl.textContent = `${total} item${total === 1 ? '' : 's'}`;
    }
    if (this.submitButton) {
      this.submitButton.disabled = total === 0;
    }

    document.dispatchEvent(
      new CustomEvent('decoration:quantity-changed', {
        bubbles: true,
        detail: { quantity: total, productId: this.productId },
      })
    );

    return total;
  }

  _getSelectedDecorationValue() {
    // the decoration selector keeps a summary such as "Print: Left chest (...); Embroidery - new logo: Full back (...)"
    const field = document.querySelector('[name="properties[Decoration]"]');
    return field?.value || 'No decoration';
  }

  _getDecorationRef() {
    return document.querySelector('[name="properties[_decoration_ref]"]')?.value || '';
  }

  _getSelectedDecorationCode(value) {
    if (value.startsWith('Print')) return 'print';
    if (value.startsWith('Embroidery - new logo')) return 'embroidery_new';
    if (value.startsWith('Embroidery - logo on file')) return 'embroidery_existing';
    return 'none';
  }

  _setNote(message, variant) {
    if (!this.noteEl) return;
    this.noteEl.textContent = message;
    this.noteEl.hidden = !message;
    this.noteEl.classList.remove('size-quantity-grid__note--error', 'size-quantity-grid__note--success');
    if (variant) this.noteEl.classList.add(`size-quantity-grid__note--${variant}`);
  }

  async _handleSubmit() {
    // Ignore repeat clicks while an add is already in progress.
    if (this._submitting) return;
    this._submitting = true;
    try {
      await this._addSelectedSizes();
    } finally {
      this._submitting = false;
    }
  }

  async _addSelectedSizes() {
    const inputs = this._getRowInputs();
    const items = inputs
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Math.max(0, parseInt(input.value, 10) || 0),
      }))
      .filter((item) => item.quantity > 0);

    if (items.length === 0) return;

    const decorationValue = this._getSelectedDecorationValue();
    const decorationCode = this._getSelectedDecorationCode(decorationValue);
    if (decorationCode !== 'none') {
      items.forEach((item) => {
        item.properties = { Decoration: decorationValue };
        const ref = this._getDecorationRef();
        if (ref) item.properties._decoration_ref = ref;  // links these garments to their decoration fee lines
      });
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    this.submitButton.disabled = true;
    this._setNote('Adding to cart…');

    if (decorationCode !== 'none') {
      document.dispatchEvent(
        new CustomEvent('decoration:register-submission', {
          bubbles: true,
          detail: { quantity: totalQuantity, productId: this.productId },
        })
      );
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        this._setNote(errorBody?.description || 'Could not add those sizes to your cart. Please try again.', 'error');
        this.submitButton.disabled = false;
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id || 'size-quantity-grid',
            data: { source: 'size-grid', productId: this.productId, itemCount: cart.item_count },
          },
        })
      );

      inputs.forEach((input) => {
        input.value = '0';
      });
      this._updateTotal();
      this._setNote(`Added ${totalQuantity} item${totalQuantity === 1 ? '' : 's'} to your cart.`, 'success');
    } catch (error) {
      console.error('Failed to add sizes to cart', error);
      this._setNote('Could not add those sizes to your cart. Please try again.', 'error');
      this.submitButton.disabled = false;
    }
  }
}

customElements.define('size-quantity-grid-component', SizeQuantityGridComponent);
